<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12" md="4"
        class="flex-column justify-space-between position-relative"
        :class="[isOpenedLeftSide ? 'd-flex' : 'd-none']"
        :key="'left_' + resetKey"
      >
        <ProjectsList
          v-model="selectedTasksAndFolders"
          :updateProjectsList="updateProjectsList"
          :redirectedProject="redirectedProject"
          :redirectedClient="redirectedClient"
          :restoredState="projectsRestoredState"
          @projectsListUpdated="onProjectListUpdated"
          @addReporting="addReporting"
          @changeFilterClosedFoldersTasks="onFilterClosedChanged"
          @getReportsOnRedirect="getReportsOnRedirect"
          @persistProjectsUi="onPersistProjectsUi"
        />
        <EmployeeList
          v-model="selectedEmployee"
          :redirectedEmployee="redirectedEmployee"
          :restoredState="employeeRestoredState"
          @getReportsOnRedirect="getReportsOnRedirect"
          @persistEmployeeUi="onPersistEmployeeUi"
        />
      </v-col>
      <v-col cols="12" :md="isOpenedLeftSide ? 8 : 12" class="position-relative">
        <v-btn color="primary"
          icon
          class="toggle-left-side-btn"
          :class="{'forward-btn': !isOpenedLeftSide}"
          @click="isOpenedLeftSide = !isOpenedLeftSide"
        >
          <v-icon>{{ isOpenedLeftSide ? 'mdi-backburger' : 'mdi-forwardburger' }}</v-icon>
        </v-btn>
        <ReportsList
          v-model="reportsData"
          ref="reportsRef"
          :key="'reports_' + resetKey"
          @reportingWasAdded="reportingFor = null"
          @resetState="onResetState"
          :addReportingFor="reportingFor"
          :refreshResultsKey="refreshReportsGrid"
          :filterClosedFoldersTasks="filterClosedFoldersTasks"
          :isRedirected="isRedirected"
          :restoredState="reportsRestoredState"
          @persistReportsUi="onPersistReportsUi"
        />
      </v-col>
    </v-row>
  </v-container>
</template>
<script>
import { loadViewState, saveViewState, clearViewState, VIEW_KEYS } from '@/utils/viewStatePersistence'
import { collapseOversizedTaskSelection } from '@/utils/managerReportsSelection'

export default {
  name: 'ManagerReports',
  components: {
    ProjectsList: () => import('@/components/manager_reports/ProjectsList.vue'),
    ReportsList: () => import('@/components/manager_reports/ReportsList.vue'),
    EmployeeList: () => import('@/components/manager_reports/EmployeeList.vue')
  },
  data () {
    return {
      updateKey: 0,
      selectedTasksAndFolders: {
        selectedProjects: [],
        selectedTasks: [],
        selectedFolders: [],
        selectionScopeProjectIds: [],
        collapsedFromNestedTasks: false
      },
      selectedEmployee: {
        selectedEmployee: []
      },
      filterClosedFoldersTasks: false,
      loading: false,
      search: '',
      selects: [],
      updateProjectsList: null,
      reportingFor: null,
      isOpenedLeftSide: true,
      refreshReportsGrid: 0,
      redirectedProject: null,
      redirectedEmployee: null,
      redirectedClient: null,
      resetKey: 0,
      projectsRestoredState: null,
      employeeRestoredState: null,
      reportsRestoredState: null,
      skippingPersist: false
    }
  },
  created () {
    // Apply before children mount so restoredState props are available in their mounted()
    if (this.$route.params.Project_ID) {
      this.redirectedProject = this.$route.params.Project_ID
    } else if (this.$route.params.Employee_ID) {
      this.redirectedEmployee = this.$route.params.Employee_ID
    } else if (this.$route.params.Client_ID) {
      this.redirectedClient = this.$route.params.Client_ID
    }

    if (!this.isRedirected) {
      this.applySavedState(loadViewState(VIEW_KEYS.managerReports, this.userLogin))
    }
  },
  computed: {
    user () {
      return this.$store.getters.getUser
    },
    userLogin () {
      return this.user && this.user.login
    },
    reportsData () {
      return {
        ...this.selectedTasksAndFolders,
        ...this.selectedEmployee
      }
    },
    isRedirected () {
      return this.$route.params.Project_ID || this.$route.params.Employee_ID || this.$route.params.Client_ID
    }
  },
  methods: {
    applySavedState (saved) {
      if (!saved || typeof saved !== 'object' || !Object.keys(saved).length) return

      this.skippingPersist = true

      if (saved.selectedTasksAndFolders) {
        this.selectedTasksAndFolders = {
          selectedProjects: Array.isArray(saved.selectedTasksAndFolders.selectedProjects)
            ? [...saved.selectedTasksAndFolders.selectedProjects]
            : [],
          selectedTasks: Array.isArray(saved.selectedTasksAndFolders.selectedTasks)
            ? [...saved.selectedTasksAndFolders.selectedTasks]
            : [],
          selectedFolders: Array.isArray(saved.selectedTasksAndFolders.selectedFolders)
            ? [...saved.selectedTasksAndFolders.selectedFolders]
            : [],
          selectionScopeProjectIds: Array.isArray(saved.selectedTasksAndFolders.selectionScopeProjectIds)
            ? [...saved.selectedTasksAndFolders.selectionScopeProjectIds]
            : [],
          collapsedFromNestedTasks: !!saved.selectedTasksAndFolders.collapsedFromNestedTasks
        }
      }

      if (saved.selectedEmployee) {
        this.selectedEmployee = {
          selectedEmployee: Array.isArray(saved.selectedEmployee.selectedEmployee)
            ? [...saved.selectedEmployee.selectedEmployee]
            : (Array.isArray(saved.selectedEmployee) ? [...saved.selectedEmployee] : [])
        }
      }

      if (typeof saved.filterClosedFoldersTasks === 'boolean') {
        this.filterClosedFoldersTasks = saved.filterClosedFoldersTasks
      }
      if (typeof saved.isOpenedLeftSide === 'boolean') {
        this.isOpenedLeftSide = saved.isOpenedLeftSide
      }

      this.projectsRestoredState = {
        search: saved.projectsSearch || '',
        filterClosedFoldersTasks: !!saved.filterClosedFoldersTasks,
        selectedProjects: this.selectedTasksAndFolders.selectedProjects,
        selectedTasks: this.selectedTasksAndFolders.selectedTasks,
        selectedFolders: this.selectedTasksAndFolders.selectedFolders,
        selectionScopeProjectIds: this.selectedTasksAndFolders.selectionScopeProjectIds,
        collapsedFromNestedTasks: !!this.selectedTasksAndFolders.collapsedFromNestedTasks
      }

      this.employeeRestoredState = {
        search: saved.employeeSearch || '',
        selectedLabels: Array.isArray(saved.selectedLabels) ? [...saved.selectedLabels] : [],
        selectAllLabels: !!saved.selectAllLabels,
        selectedEmployee: this.selectedEmployee.selectedEmployee
      }

      this.reportsRestoredState = {
        dateRange: Array.isArray(saved.dateRange) ? [...saved.dateRange] : [],
        groupBy: Array.isArray(saved.groupBy) ? [...saved.groupBy] : [],
        search: saved.reportsSearch || ''
      }

      this.$nextTick(() => {
        this.skippingPersist = false
      })
    },
    persist (patch) {
      if (this.skippingPersist || this.isRedirected || !this.userLogin) return
      saveViewState(VIEW_KEYS.managerReports, this.userLogin, patch)
    },
    persistSelections () {
      const collapsed = collapseOversizedTaskSelection({
        selectedProjects: this.selectedTasksAndFolders.selectedProjects || [],
        selectedTasks: this.selectedTasksAndFolders.selectedTasks || [],
        selectedFolders: this.selectedTasksAndFolders.selectedFolders || [],
        selectionScopeProjectIds: this.selectedTasksAndFolders.selectionScopeProjectIds || []
      })

      this.persist({
        selectedTasksAndFolders: collapsed,
        selectedEmployee: {
          selectedEmployee: this.selectedEmployee.selectedEmployee || []
        },
        filterClosedFoldersTasks: this.filterClosedFoldersTasks,
        isOpenedLeftSide: this.isOpenedLeftSide
      })
    },
    onFilterClosedChanged (value) {
      this.filterClosedFoldersTasks = value
      this.persist({ filterClosedFoldersTasks: value })
    },
    onPersistProjectsUi (ui) {
      this.persist({
        projectsSearch: ui.search || ''
      })
    },
    onPersistEmployeeUi (ui) {
      this.persist({
        employeeSearch: ui.search || '',
        selectedLabels: ui.selectedLabels || [],
        selectAllLabels: !!ui.selectAllLabels
      })
    },
    onPersistReportsUi (ui) {
      this.persist({
        dateRange: ui.dateRange || [],
        groupBy: ui.groupBy || [],
        reportsSearch: ui.search || ''
      })
    },
    onResetState () {
      clearViewState(VIEW_KEYS.managerReports, this.userLogin)
      this.skippingPersist = true
      this.selectedTasksAndFolders = {
        selectedProjects: [],
        selectedTasks: [],
        selectedFolders: [],
        selectionScopeProjectIds: [],
        collapsedFromNestedTasks: false
      }
      this.selectedEmployee = { selectedEmployee: [] }
      this.filterClosedFoldersTasks = false
      this.isOpenedLeftSide = true
      this.reportingFor = null
      this.projectsRestoredState = null
      this.employeeRestoredState = null
      this.reportsRestoredState = null
      this.resetKey += 1
      this.$nextTick(() => {
        this.skippingPersist = false
      })
    },
    onProjectListUpdated () {
      this.updateProjectsList = null
      ++this.refreshReportsGrid
    },
    addReporting (item) {
      this.reportingFor = { ...item }
    },
    getReportsOnRedirect () {
      this.$refs.reportsRef.getRedirectedReports()
    }
  },
  watch: {
    selectedTasksAndFolders: {
      deep: true,
      handler () {
        this.persistSelections()
      }
    },
    selectedEmployee: {
      deep: true,
      handler () {
        this.persistSelections()
      }
    },
    isOpenedLeftSide (val) {
      this.persist({ isOpenedLeftSide: val })
    }
  }
}
</script>

<style lang="scss">
  .position-relative {
    position: relative;
  }
  .toggle-left-side-btn {
    position: absolute;
    top: 15px;
    left: 0;
    transform: translate(-50%, -30%);
    z-index: 3;
    &.forward-btn {
      transform: translate(10%, -30%);
    }
  }
</style>
